import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, BookMarked, Users, ShieldCheck } from "lucide-react";
import { Button } from "../components/ui/button";

const HERO_IMG = "https://customer-assets-agu9un31.emergentagent.net/job_publishing-platform-2/artifacts/hhfbg78l_47E1B9FF-E561-4742-B0E4-7C1F20BECE68_L0_001-5_20_2026%2C%204_33_07%20PM.png";

const BOOKS = [
  "https://images.unsplash.com/photo-1620233977581-509e4c6b0636?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
  "https://images.unsplash.com/photo-1714146997154-c3500cfdd39c?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
  "https://images.unsplash.com/photo-1598024055266-e772a5f8c128?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
];

export default function Landing() {
  return (
    <div>
      {/* Hero */}
      <section className="grain relative overflow-hidden">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-16 pb-20 lg:pt-24 lg:pb-28 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="overline mb-4" data-testid="hero-overline">Faraway Sister Haven · A Place to connect Authors and Readers</div>
            <h1 className="rainbow-text text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              Where <em className="italic">Authors</em> and
              <br className="hidden sm:block" /> <em className="italic">Readers</em> meet before and after publication.
            </h1>
            <p className="mt-6 text-base sm:text-lg text-slate-600 max-w-xl leading-relaxed">
              Authors post book campaigns, readers can apply. Review the applications and select your readers.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/campaigns">
                <Button
                  size="lg"
                  className="btn-press bg-[color:var(--oxford)] hover:bg-[color:var(--oxford-hover)] text-white rounded-full px-6"
                  data-testid="hero-browse-btn"
                >
                  Browse ARC Campaigns <ArrowRight size={16} className="ml-2" />
                </Button>
              </Link>
              <Link to="/register">
                <Button
                  size="lg"
                  variant="outline"
                  className="btn-press rounded-full px-6 border-slate-300"
                  data-testid="hero-post-btn"
                >
                  Post a Campaign
                </Button>
              </Link>
            </div>

            <div className="mt-10 flex flex-wrap gap-x-8 gap-y-4 text-sm text-slate-600">
              <div className="flex items-center gap-2"><Sparkles size={16} className="text-amber-600" /> Free Forever</div>
              <div className="flex items-center gap-2"><ShieldCheck size={16} className="text-emerald-600" /> Approval-based trust</div>
              <div className="flex items-center gap-2"><BookMarked size={16} className="text-indigo-700" /> Authors can choose to send physical or ebook</div>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="relative rounded-2xl overflow-hidden" style={{ border: "1px solid var(--border-soft)", boxShadow: "0 30px 60px -20px rgba(0,0,0,0.8), 0 0 0 1px rgba(224,64,251,0.15)" }} data-testid="hero-brand-image">
              <img
                src={HERO_IMG}
                alt="Faraway Sister Haven — the sisters"
                className="w-full h-auto object-cover block"
              />
            </div>
          </div>
        </div>
        <div className="hero-line" />
      </section>

      {/* How it Works */}
      <section id="how" className="max-w-6xl mx-auto px-4 sm:px-6 py-16 lg:py-24">
        <div className="overline mb-3">How it works</div>
        <h2 className="font-serif text-3xl sm:text-4xl tracking-tight mb-10 max-w-2xl">
          A better ritual for launching &amp; reviewing books.
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            {
              title: "Authors post campaigns",
              body: "Add your book, pen name, format (ebook, physical, or both), copies available, and review deadline.",
              icon: <BookMarked className="text-indigo-800" />,
            },
            {
              title: "Readers apply",
              body: "Browse open campaigns and apply with an optional note about why you'd love to review.",
              icon: <Users className="text-amber-700" />,
            },
            {
              title: "Trust & auto-accept",
              body: "Authors approve or reject. Trusted readers can auto-accept future campaigns with agreement.",
              icon: <ShieldCheck className="text-emerald-700" />,
            },
          ].map((s, i) => (
            <div key={s.title} className="paper-card p-6" data-testid={`how-step-${i}`}>
              <div className="h-10 w-10 rounded-lg bg-slate-50 border flex items-center justify-center mb-4" style={{ borderColor: "var(--border-soft)" }}>
                {s.icon}
              </div>
              <div className="overline mb-2">Step {i + 1}</div>
              <h3 className="font-serif text-xl mb-2">{s.title}</h3>
              <p className="text-sm text-slate-600 leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 pb-24">
        <div className="paper-card p-8 sm:p-12 flex flex-col sm:flex-row gap-6 items-start sm:items-center justify-between" style={{ background: "linear-gradient(135deg, #FFF9EE 0%, #FDFBF7 100%)" }}>
          <div>
            <h3 className="font-serif text-2xl sm:text-3xl tracking-tight">Ready to find your next reader — or your next favorite book?</h3>
            <p className="text-slate-600 mt-2 text-sm sm:text-base">Sign up in 30 seconds. Choose your role. Start reading or getting reviewed.</p>
          </div>
          <Link to="/register">
            <Button size="lg" className="btn-press bg-[color:var(--oxford)] hover:bg-[color:var(--oxford-hover)] text-white rounded-full px-8" data-testid="cta-signup-btn">
              Create free account <ArrowRight size={16} className="ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
